import React, { useEffect, useState, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { useAppContext } from '../../contexts/AppContext';
import { useSelection } from '../../hooks/useSelection';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icon in React-Leaflet
import L from 'leaflet';
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function MapTab() {
  const { files, setFiles } = useAppContext();
  const { primaryIndex } = useSelection();
  const [position, setPosition] = useState([37.7749, -122.4194]); // Default: San Francisco
  const [hasGPS, setHasGPS] = useState(false);
  const [mapKey, setMapKey] = useState(0);

  // Memoize GPS coordinates to prevent unnecessary re-renders
  const gpsCoordinates = useMemo(() => {
    if (primaryIndex >= 0 && files[primaryIndex]) {
      const exif = files[primaryIndex].modifiedExifData;
      if (exif.latitude && exif.longitude) {
        return {
          lat: exif.latitude,
          lon: exif.longitude,
          hasGPS: true
        };
      }
    }
    return { lat: null, lon: null, hasGPS: false };
  }, [primaryIndex, files[primaryIndex]?.modifiedExifData?.latitude, files[primaryIndex]?.modifiedExifData?.longitude]);

  useEffect(() => {
    if (gpsCoordinates.hasGPS) {
      const newPosition = [gpsCoordinates.lat, gpsCoordinates.lon];
      setPosition(newPosition);
      setHasGPS(true);
      setMapKey(prev => prev + 1); // Force map to re-center on new position
    } else {
      // Reset to default position when photo has no GPS
      setPosition([37.7749, -122.4194]); // San Francisco
      setHasGPS(false);
      setMapKey(prev => prev + 1); // Force map to re-center to default
    }
  }, [gpsCoordinates]);

  const handleMarkerDragEnd = (e) => {
    if (primaryIndex < 0) return;

    const newLatLng = e.target.getLatLng();
    setPosition([newLatLng.lat, newLatLng.lng]);

    // Update the file's GPS data
    setFiles(prev => prev.map((f, i) => {
      if (i === primaryIndex) {
        return {
          ...f,
          modifiedExifData: {
            ...f.modifiedExifData,
            latitude: newLatLng.lat,
            longitude: newLatLng.lng
          },
          hasChanges: true
        };
      }
      return f;
    }));
  };

  if (primaryIndex < 0) {
    return (
      <div className="map-placeholder">
        <p>Select a file to set GPS coordinates</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="relative h-[280px] rounded-md overflow-hidden border border-border-default/70 shadow-sm group">
        <MapContainer
          center={position}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          key={mapKey}
          zoomControl={true}
          scrollWheelZoom={false}
          dragging={true}
          touchZoom={true}
          doubleClickZoom={true}
          boxZoom={true}
          keyboard={true}
          attributionControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {hasGPS && (
            <Marker
              position={position}
              draggable={true}
              eventHandlers={{
                dragend: handleMarkerDragEnd
              }}
            />
          )}
        </MapContainer>
        {!hasGPS && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-bg-deep/70 backdrop-blur-[1px] p-8 transition-all group-hover:bg-bg-deep/60">
            <div className="text-center space-y-2">
              <p className="text-[13px] font-bold text-text-primary">No location set</p>
              <p className="text-[10px] text-text-supporting font-sans leading-relaxed max-w-[180px]">
                Use the location search or coordinate inputs to set a location
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(MapTab);
